import { useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { StatusBadge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { deleteAllItems, deleteItem, fetchItemCategories, fetchItemCollections, importItems, itemsExportPath, itemsImportTemplatePath, listItems, type Item } from '@/api/items';
import { apiError } from '@/lib/apiError';
import { formatINRSymbol } from '@/lib/format';
import { downloadExcel } from '@/lib/downloadExcel';
import { ItemFormModal } from './ItemFormModal';
import { Download, FileDown, Search, Trash2, Upload, ImageIcon, X } from '@/components/icons';
import { ItemCodesModal } from './ItemCodesModal';
import { ExcelImportModal } from '@/components/ui/ExcelImportModal';
import { useAuth } from '@/auth/useAuth';
import type { SortState } from '@/components/ui/ColumnPicker';

/** Columns Item Master can be sorted by (the whole list, on the server). */
const ITEM_SORTS = [
  { key: 'name', label: 'Item Name' }, { key: 'code', label: 'Code' }, { key: 'category', label: 'Category' },
  { key: 'collection', label: 'Collection' }, { key: 'sizeUnit', label: 'Size Unit' }, { key: 'hsn', label: 'HSN' },
  { key: 'gstPct', label: 'GST%' }, { key: 'mrp', label: 'MRP' }, { key: 'stock', label: 'Stock' },
  { key: 'reorderLevel', label: 'Reorder' }, { key: 'status', label: 'Status' }, { key: 'barcode', label: 'Barcode' },
  { key: 'createdAt', label: 'Date added' },
];

export function ItemsPage() {
  const qc = useQueryClient();
  // Editing an existing item is a separate permission-matrix switch.
  const canEdit = useAuth().hasPerm('items-edit');
  const toast = useToast();
  const confirmDlg = useConfirm();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [collection, setCollection] = useState('');
  // Sort chosen in the column picker — remembered like the column choices, applied on the server.
  const [sort, setSortState] = useState<SortState | null>(() => {
    try { const s = JSON.parse(localStorage.getItem('table.items.sort') ?? 'null'); return s && s.key ? s : null; } catch { return null; }
  });
  const setSort = (s: SortState | null) => {
    setSortState(s);
    setPage(1);
    try { if (s) localStorage.setItem('table.items.sort', JSON.stringify(s)); else localStorage.removeItem('table.items.sort'); } catch { /* storage blocked */ }
  };
  const [status, setStatus] = useState<'' | 'Active' | 'Inactive'>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Item | null>(null);
  const [codesItem, setCodesItem] = useState<Item | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [preview, setPreview] = useState<{ url: string; label: string } | null>(null);

  const filter = useMemo(
    () => ({
      search: search || undefined,
      category: category || undefined,
      collection: collection || undefined,
      status: status || undefined,
      sort: sort?.key,
      dir: sort?.dir,
      page,
      pageSize,
    }),
    [search, category, collection, status, sort, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['items', filter],
    queryFn: () => listItems(filter),
    placeholderData: keepPreviousData,
  });

  // Category filter options = only categories that actually belong to items.
  const categoriesQuery = useQuery({
    queryKey: ['item-categories'],
    queryFn: fetchItemCategories,
    staleTime: 0, // always reflect the live Item Master
    refetchOnWindowFocus: true,
  });

  const collectionsQuery = useQuery({
    queryKey: ['item-collections'],
    queryFn: fetchItemCollections,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const delMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      toast.push('Item deleted', 'info');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  const delAllMutation = useMutation({
    mutationFn: deleteAllItems,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['items'] });
      toast.push(`Deleted ${res.deleted} items`, 'info');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Delete failed'), 'error');
    },
  });

  async function onDeleteAll() {
    if (!await confirmDlg(`Delete ALL ${total} items? This cannot be undone.`)) return;
    delAllMutation.mutate();
  }

  function openAdd() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(item: Item) {
    setEditing(item);
    setModalOpen(true);
  }
  async function onDelete(item: Item) {
    if (!await confirmDlg(`Delete item "${item.name}"? This cannot be undone.`)) return;
    delMutation.mutate(item.id);
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Item Master"      />

      <FilterBar>
        <FilterField label="Search" className="sm:col-auto">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search items"
            />
          </div>
        </FilterField>
        <FilterField label="Category">
          <Select
            value={category}
            onChange={(e) => { setCategory(e.target.value); setPage(1); }}
            className="sm:min-w-[200px]"
          >
            <option value="">All Categories</option>
            {(categoriesQuery.data ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Collection">
          <Select
            value={collection}
            onChange={(e) => { setCollection(e.target.value); setPage(1); }}
            className="sm:min-w-[180px]"
          >
            <option value="">All Collections</option>
            {(collectionsQuery.data ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Status">
          <Select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as '' | 'Active' | 'Inactive');
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </Select>
        </FilterField>
        {/* Merged chip strip: visible on both viewports, right-aligned inside the filter block. */}
        <div className="col-span-3 flex flex-nowrap gap-1.5 [&>*]:flex-auto [&>*]:whitespace-nowrap sm:col-span-1 sm:ml-auto sm:items-end sm:self-end sm:[&>*]:flex-none">
          <button
            type="button"
            onClick={() => downloadExcel(itemsExportPath(filter), 'GRHAMOY_Items.xlsx')}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-success hover:text-success sm:h-8 sm:px-3 sm:text-xs"
          >
            <Download size={12} /> Export
          </button>
          <button
            type="button"
            onClick={() => downloadExcel(itemsImportTemplatePath(), 'GRHAMOY_Items_Template.xlsx')}
            title="Download a sample Excel with the correct columns and one example row"
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <FileDown size={12} /> Template
          </button>
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:h-8 sm:px-3 sm:text-xs"
          >
            <Upload size={12} /> Import
          </button>
          <button
            type="button"
            onClick={onDeleteAll}
            disabled={delAllMutation.isPending || total === 0}
            className="inline-flex h-7 items-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-danger shadow-sm transition hover:border-danger hover:text-danger disabled:opacity-50 sm:h-8 sm:px-3 sm:text-xs"
          >
            {delAllMutation.isPending ? (
              'Deleting…'
            ) : (
              <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Delete All</span>
            )}
          </button>
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex h-7 items-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:h-8 sm:px-3 sm:text-xs"
          >
            ＋ Add Item
          </button>
        </div>
      </FilterBar>

      <Table<Item>
        storageKey="items"
        title="Item Master"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        sortOptions={ITEM_SORTS}
        sort={sort}
        onSortChange={setSort}
        columns={[
          {
            key: 'image',
            header: 'Image',
            width: '64px',
            cell: (r) =>
              r.imageUrl ? (
                <button
                  type="button"
                  onClick={() => setPreview({ url: r.imageUrl!, label: `${r.code} · ${r.name}` })}
                  title="Click to preview"
                  className="group relative h-7 w-7 overflow-hidden rounded-md border border-[var(--border-color)]"
                >
                  <img src={r.imageUrl} alt={r.name} className="h-7 w-7 object-cover transition group-hover:scale-105" />
                </button>
              ) : (
                <div className="flex h-7 w-7 items-center justify-center rounded-md border border-[var(--border-color)] bg-white/[0.04] text-[var(--text-muted)]">
                  <ImageIcon size={14} className="opacity-40" />
                </div>
              ),
          },
          { key: 'code', header: 'Code', cell: (r) => <span className="text-xs">{r.code}</span> },
          { key: 'name', header: 'Item Name', cell: (r) => <span className="font-bold">{r.name}</span> },
          { key: 'category', header: 'Category', cell: (r) => r.category },
          { key: 'collection', header: 'Collection', cell: (r) => r.collection || '—' },
          { key: 'sizeUnit', header: 'Size Unit', cell: (r) => r.sizeUnit ?? '—' },
          { key: 'hsn', header: 'HSN', cell: (r) => r.hsn ?? '—' },
          { key: 'gst', header: 'GST%', cell: (r) => `${Number(r.gstPct)}%`, align: 'right' },
          { key: 'mrp', header: 'MRP', cell: (r) => formatINRSymbol(r.mrp), align: 'right' },
          {
            key: 'stock',
            header: 'Stock',
            align: 'right',
            cell: (r) => {
              // Treat a missing stock row as 0 so every item shows a stock
              // number rather than a dash.
              const qty = r.warehouseStock ? Number(r.warehouseStock.currentQty) : 0;
              const reorder = r.warehouseStock?.reorderLevel ?? r.reorderLevel ?? null;
              const color = qty <= 0 ? '#ef4444' : reorder != null && qty <= reorder ? '#f59e0b' : '#22c55e';
              return (
                <span style={{ color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {qty} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 11 }}>{r.unit}</span>
                </span>
              );
            },
          },
          { key: 'reorder', header: 'Reorder', cell: (r) => r.reorderLevel ?? '—', align: 'right' },
          { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
          {
            key: 'barcode',
            header: 'Barcode',
            width: '170px',
            align: 'center',
            cell: (r) => (
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-[11px] text-[var(--text-secondary)]" title={r.barcode ?? ''}>
                  {r.barcode || '—'}
                </span>
                <Button variant="glass" size="sm" onClick={() => setCodesItem(r)} title="Barcode / QR">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                    <rect x="5" y="5" width="3" height="3"/><rect x="16" y="5" width="3" height="3"/><rect x="5" y="16" width="3" height="3"/>
                    <path d="M14 14h3v3h-3z"/><path d="M17 17h4"/><path d="M21 14v3"/><path d="M14 17v4"/><path d="M17 21h4"/>
                  </svg>
                </Button>
              </div>
            ),
          },
          {
            key: 'actions',
            header: '',
            width: '110px',
            cell: (r) => (
              <div className="flex gap-1.5">
                {canEdit && <Button variant="glass" size="sm" onClick={() => openEdit(r)}>Edit</Button>}
                <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete item"><Trash2 size={14} /></Button>
              </div>
            ),
          },
        ]}
        footer={<Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />}
      />

      <ItemFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} />
      <ItemCodesModal
        open={codesItem != null}
        item={codesItem}
        onClose={() => setCodesItem(null)}
      />
      <ExcelImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Items"
        expectedColumns={['Code', 'Name*', 'Category*', 'Unit*', 'HSN', 'GST%', 'MRP*', 'Dist Price', 'Dealer Price', 'Purchase Rate', 'Barcode', 'Color', 'Brand', 'Style', 'Collection', 'Reorder', 'Status', 'Width*', 'Length*', 'Size Unit*', 'GSM*', 'Net Weight (kg)*']}
        onImport={importItems}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['items'] })}
      />

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPreview(null)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -right-3 -top-3 rounded-full bg-[var(--bg-primary)] p-1.5 text-[var(--text-primary)] shadow-lg ring-1 ring-[var(--border-color)] hover:text-danger"
              title="Close"
            >
              <X size={18} />
            </button>
            <img
              src={preview.url}
              alt={preview.label}
              className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
            />
            <div className="mt-2 text-center text-xs font-semibold text-white/90">{preview.label}</div>
          </div>
        </div>
      )}
    </div>
  );
}
